import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Calendar, CreditCard, Eye, Mail, Phone, Search } from "lucide-react";
import { Button } from "../../../components/ui/button";
import ThemeTable from "../../../components/Table/ThemeTable";
import { getAllCompanyUsers } from "../../../services/apiservices/employee";
import { Badge } from "../../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import SingleSelectNew from "../../../components/SelectField/SingleSelectNew";
import { DatePickerWithRange } from "./DatePicker";
import { subDays } from "date-fns";
import moment from "moment";
import { findSubscriptionByCustomerId } from "../../../services/apiservices/users";
import { useMediaQuery } from "@mui/material";

const MobileUserCard = ({
  user,
  getRoleBadgeColor,
  getPlanBadgeColor,
  handleNaviage,
}) => (
  <Card className="mb-16">
    <CardContent className="p-16">
      <div className="flex justify-between items-start mb-12">
        <div className="flex-1 min-w-0">
          <h3
            className="font-medium text-sm md:text-base truncate"
            style={{ lineHeight: "20px" }}
          >
            {user?.name}
          </h3>
        </div>
        <Badge
          className={`${getRoleBadgeColor(user?.type)} text-xs`}
          style={{ borderRadius: "6px" }}
          variant={"info"}
        >
          {user?.type?.charAt(0)?.toUpperCase() + user?.type?.slice(1)}
        </Badge>
      </div>

      <div className="space-y-2 text-xs md:text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500 flex items-center">
            <Mail className="w-3 h-3 mr-4" />
            Email:
          </span>
          <a
            href={`mailto:${user?.email}`}
            className="text-blue-600 hover:underline truncate max-w-[180px] text-right"
            style={{ color: "blue" }}
          >
            {user?.email}
          </a>
        </div>

        {user?.contact && (
          <div className="flex items-center justify-between">
            <span className="text-gray-500 flex items-center">
              <Phone className="w-3 h-3 mr-4" />
              Contact:
            </span>
            <span className="truncate max-w-[180px] text-right">
              {user?.contact}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <span className="text-gray-500 flex items-center">
            <CreditCard className="w-3 h-3 mr-4" />
            Plan:
          </span>
          <div className="text-right">
            <div className="flex items-center justify-end">
              <Badge
                className={`${getPlanBadgeColor(user?.planName)} text-xs mr-8`}
                style={{ borderRadius: "6px" }}
                variant={"info"}
              >
                {user?.planName}
              </Badge>
              <span className="text-xs text-gray-600">
                {user?.interval === "month" ? "Monthly" : "Yearly"}
              </span>
            </div>
            <div className="text-xs font-medium">{user?.amount}</div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-500 flex items-center">
            <Calendar className="w-3 h-3 mr-4" />
            Next Payment:
          </span>
          <span className="text-xs text-right">{user?.nextBillingDate}</span>
        </div>
      </div>

      <div className="mt-16 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => handleNaviage(user?.id)}
        >
          <Eye className="h-3 w-3 mr-4" />
          View
        </Button>
      </div>
    </CardContent>
  </Card>
);

const Accounts = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("Email");
  const [timeFilter, setTimeFilter] = useState("all");
  const [companyData, setCompanyData] = useState();
  const [activeData, setActiveUserData] = useState();
  const [deActiveData, setDeActiveUserData] = useState();
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already handled by the useEffect
  };

  const columns = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
      isSortable: true,
      gray: true,
      className: "pl-20",
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
      isSortable: true,
      gray: true,
    },
    {
      id: "contact",
      numeric: false,
      disablePadding: false,
      label: "Contact",
      isSortable: true,
      gray: true,
    },
    {
      id: "type",
      numeric: false,
      disablePadding: false,
      label: "Account Type",
      isSortable: true,
      gray: true,
    },
    {
      id: "plan",
      numeric: false,
      disablePadding: false,
      label: "Plan & Payment",
      isSortable: true,
      gray: true,
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
      isSortable: true,
      gray: true,
    },
  ];

  const typeOptions = [
    { label: "Email", value: "Email" },
    { label: "Name", value: "Name" },
    { label: "Account Type", value: "AccountType" },
  ];

  const timeFilters = [
    { label: "Today", value: "today" },
    { label: "Last 7 Days", value: "7days" },
    { label: "Last 30 Days", value: "30days" },
    { label: "Last 90 Days", value: "90days" },
    { label: "All Time", value: "all" },
  ];

  const formatDate = (timestamp) =>
    timestamp ? moment.unix(timestamp).format("MM/DD/YYYY") : "N/A";
  const formatStripeAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const fetchCompanyUsers = async () => {
    try {
      const [companyUsersRes, subscriptionRes] = await Promise.all([
        getAllCompanyUsers(),
        findSubscriptionByCustomerId(),
      ]);

      const newData = (companyUsersRes?.data || []).map((item) => {
        const industry = subscriptionRes?.data?.find(
          (ind) => ind?.customer === item?.customer
        );
        const activeSub = item?.subscriptions?.data?.[0];

        return {
          ...item,
          // name: `${item?.firstName} ${item?.lastName}`,
          name: item?.companyName,
          email: item.email,
          industry: industry?.title || "N/A",
          type: item?.role === "companyUser" ? "Company Manager" : item.role,
          contact: item?.phoneNumber ? item?.phoneNumber : item?.phone,
          planName: activeSub?.plan?.name || industry?.status || "N/A",
          interval:
            activeSub?.plan?.interval || industry?.plan?.interval || "N/A",
          amount: formatStripeAmount(
            activeSub?.plan?.amount || industry?.plan?.amount || 0
          ),
          nextBillingDate: formatDate(
            activeSub?.current_period_end || industry?.current_period_end
          ),
        };
      });

      const activeData = newData.filter((item) => item.status === true);
      const deActiveData = newData.filter((item) => item.status !== true);
      setActiveUserData(activeData);
      setDeActiveUserData(deActiveData);
      setCompanyData(newData);
    } catch (error) {
      console.error("Error in fetching data:", error);
    }
  };

  useEffect(() => {
    fetchCompanyUsers();
  }, []);

  const getRoleBadgeColor = (role) => {
  const roles = Array.isArray(role) ? role : [role];

    if (roles.includes("admin")) return "bg-green-500 hover:bg-green-600";
    if (roles.includes("Company Manager") || roles.includes("companyUser"))
      return "bg-blue-500 hover:bg-blue-600";
    if (roles.includes("user")) return "bg-gray-500 hover:bg-gray-600";

    return "bg-slate-500 hover:bg-slate-600";
  };

  const getPlanBadgeColor = (planName) => {
    switch (planName?.toLowerCase()) {
      case "professional":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "plus":
        return "bg-gray-400 hover:bg-gray-500";
      case "standard":
        return "bg-orange-500 hover:bg-orange-600";
      default:
        return "bg-slate-500 hover:bg-slate-600";
    }
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
  };

  const handleNaviage = (id) => {
    navigate(`/accountsDetails/${id}`);
  };

  const renderRowCell = (rcellItem, hcellItem, index, rowindex) => {
    switch (hcellItem?.id) {
      case "type":
        return (
          <Badge
            className={getRoleBadgeColor(rcellItem?.type)}
            variant={"info"}
            style={{ borderRadius: "6px" }}
          >
            {rcellItem?.type?.charAt(0)?.toUpperCase() +
              rcellItem?.type?.slice(1)}
          </Badge>
        );
      case "name":
        return (
          <span
            className="ph-10"
            style={{ fontSize: "14px", fontWeight: "500" }}
          >
            {rcellItem?.name}{" "}
          </span>
        );
      case "email":
        return <span> {rcellItem?.email} </span>;
      case "contact":
        return <span> {rcellItem?.contact} </span>;
      case "plan":
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge
                className={getPlanBadgeColor(rcellItem?.planName)}
                style={{ borderRadius: "6px" }}
                variant={"info"}
              >
                {rcellItem?.planName === "trialing"
                  ? "Trial Plan"
                  : rcellItem?.planName}
              </Badge>
              <span className="text-sm text-gray-600">
                {rcellItem?.interval === "month" ? "Monthly" : "Yearly"}
              </span>
            </div>
            <div className="text-sm font-medium">
              {rcellItem?.planName === "trialing"
                ? "14-Days Free Trial"
                : rcellItem?.amount}
            </div>
            <div className="text-xs text-gray-500">
              Next: {rcellItem?.nextBillingDate}
            </div>
          </div>
        );
      case "actions":
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNaviage(rcellItem?.id)}
          >
            <Eye className="h-4 w-4 mr-4" />
            View
          </Button>
        );
    }
  };

  const isWithinTimeRange = (createdAt, filter) => {
    const createdDate = moment(createdAt);
    const today = moment().startOf("day");

    switch (filter) {
      case "today":
        return createdDate.isSame(today, "day");

      case "7days":
        return createdDate.isSameOrAfter(
          moment().subtract(7, "days").startOf("day")
        );

      case "30days":
        return createdDate.isSameOrAfter(
          moment().subtract(30, "days").startOf("day")
        );

      case "90days":
        return createdDate.isSameOrAfter(
          moment().subtract(90, "days").startOf("day")
        );

      case "all":
        return true;

      default:
        return true;
    }
  };

  const checkRowSearch = (searchTerm, row, searchType) => {
    const term = String(searchTerm)?.toLowerCase();
    const matchesContact = String(row?.contact)?.toLowerCase()?.includes(term);
    if (searchType === "Email") {
      return (
        String(row?.email)?.toLowerCase()?.includes(term) || matchesContact
      );
    } else if (searchType === "Name") {
      return String(row?.name)?.toLowerCase()?.includes(term) || matchesContact;
    } else if (searchType === "AccountType") {
      return String(row?.type)?.toLowerCase()?.includes(term) || matchesContact;
    }

    return (
      String(row?.email)?.toLowerCase()?.includes(term) ||
      String(row?.name)?.toLowerCase()?.includes(term) ||
      String(row?.type)?.toLowerCase()?.includes(term) ||
      matchesContact
    );
  };

  const filteredList = companyData?.filter((item) => {
    const matchesSearch =
      searchTerm !== "" ? checkRowSearch(searchTerm, item, searchType) : true;

    const matchesTime = isWithinTimeRange(item?.createdAt, timeFilter);

    return matchesSearch && matchesTime;
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-32">
        <h1 className="text-2xl font-bold mb-8 text-foreground">
          Account Management
        </h1>
        <p className="text-gray-500">Search and manage user accounts</p>
      </div>

      <div className="mb-16 md:mb-24 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-foreground">
          Account Statistics
        </h2>
        <SingleSelectNew
          contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
          itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
          placeholder={"selectType"}
          className="w-36 md:w-48"
          value={timeFilter}
          onChange={handleTimeFilterChange}
          options={timeFilters}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mb-16 md:mb-24">
        <Card className="bg-[#2c3440] text-white border-none">
          <CardContent className="pv-24 md:pt-24 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-medium text-gray-400">
                  Total Accounts
                </p>
                <h3 className="text-lg md:text-2xl font-bold">
                  {companyData?.length}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#2c3440] text-white border-none">
          <CardContent className="pv-24 md:pt-24 px-3 md:px-6">
            <p className="text-xs md:text-sm font-medium text-gray-400">
              Active Users
            </p>
            <h3 className="text-lg md:text-2xl font-bold">
              {activeData?.length}
            </h3>
          </CardContent>
        </Card>

        <Card className="bg-[#2c3440] text-white border-none">
          <CardContent className="pv-24 md:pt-24 px-3 md:px-6">
            <p className="text-xs md:text-sm font-medium text-gray-400">
              Unused Accounts
            </p>
            <h3 className="text-lg md:text-2xl font-bold">
              {deActiveData?.length}
            </h3>
          </CardContent>
        </Card>
      </div>

      <div className="mb-16 md:mb-24 bg-white p-16 md:p-24 rounded-lg shadow dark:bg-[#2c3440]">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 space-y-3 lg:space-y-0">
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <div style={{ width: isMobile ? "365px" : "180px" }}>
                  <SingleSelectNew
                    value={searchType}
                    contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                    itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                    onChange={setSearchType}
                    options={typeOptions}
                    placeholder={"Search By"}
                  />
                </div>
                <div
                  className={
                    isMobile ? "mv-8 flex-1 relative" : "flex-1 relative"
                  }
                >
                  <Input
                    type="text"
                    placeholder={`Search by ${searchType}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-40 w-full"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <Button type="submit" className="w-full sm:w-auto">
                  Search
                </Button>
              </div>
            </div>
            <div>
              <DatePickerWithRange
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow dark:bg-[#2c3440]">
        <div className="p-24 border-b ">
          <h2 className="text-lg font-medium text-foreground">
            Account Users ({companyData?.length})
          </h2>
        </div>
        {isMobile ? (
          <div className="md:hidden p-16">
            {filteredList?.length > 0 ? (
              filteredList?.map((user) => (
                <MobileUserCard
                  user={user}
                  getRoleBadgeColor={getRoleBadgeColor}
                  getPlanBadgeColor={getPlanBadgeColor}
                  handleNaviage={handleNaviage}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No accounts found matching your search criteria
              </div>
            )}
          </div>
        ) : (
          <ThemeTable
            rows={filteredList}
            headCells={columns}
            renderRowCell={renderRowCell}
            hidePagination={false}
          />
        )}
      </div>
    </div>
  );
};

export default Accounts;
